// Extract error message from response body (plain text or JSON with 'error' property)
const extractErrorMessage = async (response: Response): Promise<string> => {
  const contentType = response.headers.get("Content-Type") || ""
  if (contentType.includes("application/json")) {
    try {
      const errorJson: unknown = await response.json()
      if (
        typeof errorJson === "object" &&
        errorJson !== null &&
        Object.prototype.hasOwnProperty.call(errorJson, "error") &&
        typeof (errorJson as { error?: unknown }).error === "string"
      ) {
        return (errorJson as { error: string }).error
      } else {
        return JSON.stringify(errorJson)
      }
    } catch (e) {
      return `Fout bij lezen van JSON: ${e instanceof Error ? e.message : String(e)}`
    }
  } else {
    return await response.text()
  }
}
export type HttpMethods = "GET" | "POST" | "PUT" | "DELETE"

export class EnkApiError extends Error {
  httpStatusCode: number
  httpStatusText: string
  description: string

  constructor(httpStatusCode: number, httpStatusText: string, description: string) {
    super(`${httpStatusText} (${httpStatusCode}): ${description}`)
    this.httpStatusCode = httpStatusCode
    this.httpStatusText = httpStatusText
    this.description = description
  }
}

const isDevMode = (): boolean => {
  return window.location.hostname === "localhost" && window.location.port === "5173"
}

export const getBackendUrl = (): string => {
  if (isDevMode()) {
    return "http://localhost:18008/"
  }
  const currentUrl = new URL(window.location.href)
  return currentUrl.protocol + "//" + currentUrl.hostname + ":" + currentUrl.port.toString() + "/"
}

export const doRequest = async <ReturnType, BodyType>(method: HttpMethods, endpoint: string, body?: BodyType): Promise<ReturnType> => {
  let response: Response
  try {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const url = getBackendUrl() + endpoint
    response = await fetch(url, {
      method: method,
      headers: headers,
      body: JSON.stringify(body),
      credentials: "include",
    })
  } catch (error: unknown) {
    const reason = error instanceof Error ? (error.message === "Failed to fetch" ? "Server is niet bereikbaar" : error.message) : "unknown error"
    console.error(`Error in api call to ${endpoint}:`, error, reason)
    throw new EnkApiError(-1, `Server Api call to "${endpoint}" failed: ${reason}`, reason)
  }

  if (response.status === 300) {
    const params = await response.text()
    throw new EnkApiError(300, "need params", params)
  }

  if (response.status === 401) {
    const msg = await extractErrorMessage(response)
    throw new EnkApiError(401, "Unauthorized", msg)
  }

  if (!response.ok) {
    const errorMsg = await extractErrorMessage(response)
    throw new EnkApiError(response.status, response.statusText, errorMsg)
  }
  const contentType = response.headers.get("Content-Type")
  //if contentType is binary stream, return as arrayBuffer
  if (contentType === "application/octet-stream" || contentType === "application/pdf") {
    return (await response.arrayBuffer()) as ReturnType
  }
  if (contentType && contentType.includes("application/json")) {
    return (await response.json()) as ReturnType
  } else {
    return (await response.text()) as ReturnType
  }
}
